/**
 * Adapter that converts existing ReferenceStoreFixtures (which use the
 * older DemandSignal / VisibilitySignal / BusinessContext types) into
 * Decision Engine EvidenceSignals + BusinessContextTags.
 *
 * Kept in its own file so the Decision Engine never imports old shapes.
 */

import type { ReferenceStoreFixture } from "@/engine/harness/fixtures";
import type {
  BusinessContextTags,
  ContextTag,
  DemandDetail,
  EvidenceSignal,
  VisibilityPresenceDetail,
} from "./types";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

function tag(label: string, polarity: ContextTag["polarity"]): ContextTag {
  return {
    tag: slugify(label),
    label,
    source: "onboarding",
    polarity,
    weight: 1,
  };
}

export function fixtureToDecisionInput(fixture: ReferenceStoreFixture): {
  business_context: BusinessContextTags;
  signals: EvidenceSignal[];
  provider_versions: Record<string, string>;
} {
  const now = new Date().toISOString();

  const demandSignals: EvidenceSignal<DemandDetail>[] = fixture.demand.map((d, i) => ({
    signal_id: `demand:${slugify(d.term)}:${i}`,
    kind: "demand.search_term",
    provider: "mock-demand",
    provider_version: fixture.provider_versions["mock-demand"] ?? "0.0.0",
    subject: d.term,
    observed_at: now,
    confidence: d.local_confidence ?? 0.5,
    detail: {
      monthly_volume: d.monthly_volume,
      volume_index: d.volume_index,
      trend: d.trend ?? "flat",
      local_confidence: d.local_confidence ?? 0.5,
    },
  }));

  const visibilitySignals: EvidenceSignal<VisibilityPresenceDetail>[] = fixture.visibility.map(
    (v, i) => ({
      signal_id: `vis:${slugify(v.term)}:${i}`,
      kind: "visibility.site_presence",
      provider: "mock-visibility",
      provider_version: fixture.provider_versions["mock-visibility"] ?? "0.0.0",
      subject: v.term,
      observed_at: now,
      confidence: 0.8,
      detail: { presence: v.presence, note: v.detail },
    }),
  );

  const business_context: BusinessContextTags = {
    business_id: fixture.business_context.business_id,
    strengths: fixture.business_context.strengths.map((s) => tag(s, "positive")),
    goals: fixture.business_context.goals.map((g) => tag(g, "positive")),
    constraints: fixture.business_context.constraints.map((c) => tag(c, "negative")),
  };

  return {
    business_context,
    signals: [...demandSignals, ...visibilitySignals],
    provider_versions: fixture.provider_versions,
  };
}